import { motion } from 'framer-motion'

import { cn } from '@/lib/utils'
import {
  RevealGroup,
  RevealItem,
  fadeVariants,
  REVEAL_VIEWPORT,
} from '@/components/ui/reveal'

/** Background/ink pairs. `title` is a default — spans inside the caller's
    `title` node (e.g. HighlightText) override it. */
const TONES = {
  /** Saturated primary gradient (tailored band). Badges on it want
      `bg-app-highlight-light` — see styles.css. */
  primary: {
    section: 'bg-linear-to-r/srgb from-[#0211D9] to-[#2736F8]',
    title: 'text-white',
    description: 'text-white/80',
  },
  /** Lavender-to-white band (expert band). `/75` on the description is the
      documented 4.5:1 floor over the `#c6d2ff` corners — see styles.css. */
  light: {
    section: 'bg-expert-band',
    title: 'text-accent-foreground',
    description: 'text-accent-foreground/75',
  },
  /** Flat periwinkle band (firefighter section). Same `#c6d2ff` as the
      expert band's corners — so `/75` on the description keeps its
      documented 4.5:1 floor — but without the white plateau. */
  lavender: {
    section: 'bg-[#c6d2ff]',
    title: 'text-accent-foreground',
    description: 'text-accent-foreground/75',
  },
  /** Page background — the default for the /solutions content sections. */
  plain: {
    section: 'bg-background',
    title: 'text-accent-foreground',
    description: 'text-secondary-foreground',
  },
} as const

export type SplitSectionProps = {
  image: {
    src: string
    width: number
    height: number
    alt?: string
    className?: string
  }
  /** Replace the `<img>` grid item with a custom node (e.g. a video thumbnail
      that opens a dialog). Renders in the image column; handles its own sizing
      and `lg:order-last` for `imageSide="right"`. `image` is then ignored. */
  imageSlot?: React.ReactNode
  imageSide?: 'left' | 'right'
  tone?: keyof typeof TONES
  gridClassName?: string
  title: React.ReactNode
  titleClassName?: string
  description?: React.ReactNode
  actions?: React.ReactNode
  className?: string
  /** `false` drops the built-in image fade + copy cascade, so a caller can
      wrap the whole band in a single `Reveal` and animate it as one block. */
  reveal?: boolean
}

/**
 * Generic two-column band: image on one side, copy on the other.
 * The image renders first in the DOM so mobile always stacks it on top;
 * `imageSide="right"` only reorders the lg columns.
 */
export function SplitSection({
  image,
  imageSlot,
  imageSide = 'left',
  tone = 'plain',
  gridClassName,
  title,
  titleClassName,
  description,
  actions,
  className,
  reveal = true,
}: SplitSectionProps) {
  const ink = TONES[tone]
  /* reveal=false → render copy in plain divs so nothing animates on its own;
     the caller wraps the whole band in one Reveal instead. */
  const Group = reveal ? RevealGroup : 'div'
  const Item = reveal ? RevealItem : 'div'

  return (
    <section
      className={cn(
        'relative isolate lg:h-[var(--landing-band-h)]',
        ink.section,
        className,
      )}
    >
      {gridClassName ? (
        /* Rule grid. `inset-0` makes it exactly section-sized, which is what
           `bg-grid-lines`' calc() sizing divides into its cells. */
        <div
          aria-hidden
          className={cn(
            'bg-grid-lines pointer-events-none absolute inset-0 -z-10',
            gridClassName,
          )}
        />
      ) : null}

      {/* `grid-rows-[minmax(0,1fr)]` caps the lg row at the band's height so a
          tall image can't size the row past the band it must fit. */}
      <div className="mx-auto grid h-full max-w-7xl grid-cols-1 items-center gap-6 px-6 md:gap-8 lg:grid-cols-2 lg:grid-rows-[minmax(0,1fr)] lg:gap-12 lg:px-8">
        {imageSlot ?? (
        /* `motion.img`, not a wrapper: the image is the grid item and its
            sizing classes must live on it. Fade only, no rise: callers may run
            without `overflow-hidden`, and a 16px rise would spill the image
            over the neighbouring section. */
        <motion.img
          data-reveal
          variants={fadeVariants}
          initial={reveal ? 'hidden' : undefined}
          whileInView={reveal ? 'show' : undefined}
          viewport={reveal ? REVEAL_VIEWPORT : undefined}
          src={image.src}
          alt={image.alt ?? ''}
          aria-hidden={image.alt ? undefined : true}
          loading="lazy"
          width={image.width}
          height={image.height}
          className={cn(
            /* `justify-self-center`, not `mx-auto`: the image is a grid item,
               and justify-self merges cleanly under cn() when a caller needs
               stretch/start for a bleed (auto margins would fight `-mr-*`). */
            'pointer-events-none w-full max-w-md justify-self-center object-contain',
            imageSide === 'right' && 'lg:order-last',
            image.className,
          )}
        />
        )}

        <Group className="max-w-xl py-8 text-left md:py-12 lg:py-16">
          <Item>
            <h2
              className={cn(
                /* `leading-snug`, not the size's default 1-ish leading:
                   HighlightText pills paint the full line box, so on a
                   wrapped title tight lines make consecutive pills touch. */
                'font-serif text-2xl leading-snug font-semibold tracking-tight sm:text-3xl lg:text-4xl',
                ink.title,
                titleClassName,
              )}
            >
              {title}
            </h2>
          </Item>
          {description ? (
            <Item className="mt-6">
              <p
                className={cn(
                  'max-w-lg font-mono text-sm whitespace-pre-line',
                  ink.description,
                )}
              >
                {description}
              </p>
            </Item>
          ) : null}
          {actions ? (
            <Item className="mt-8 flex flex-col items-start gap-3 sm:flex-row sm:flex-wrap sm:items-center">
              {actions}
            </Item>
          ) : null}
        </Group>
      </div>
    </section>
  )
}
